///////////////////////////////////////////////////////////////////////////////////////////////////////////
// IMPORTS
///////////////////////////////////////////////////////////////////////////////////////////////////////////
import $ from "jquery";

import Highcharts from "highcharts";
import highchartsAccessibility from "highcharts/modules/accessibility";

// Note that the following import is required for the iframe to be resized correctly; it works in
// coordination with the iframe-resizer package that is loaded in the containing web page
import "iframe-resizer/js/iframeResizer.contentWindow";

import L from "leaflet";
import "leaflet/dist/leaflet.css";

import "leaflet-svg-shape-markers";

import Papa from "papaparse";

import * as tippy from "tippy.js";
import "tippy.js/dist/tippy.css";

import "./modules/leaflet-control-credits.css";
import LeafletCreditsControl from "./modules/leaflet-control-credits";

import "./modules/multiselect.css";
import MultiSelect from "./modules/multiselect";

import worldJson from "./data/world.json";
import dataCsv from "./data/data.csv";
import lookupCsv from "./data/lookup.csv";

///////////////////////////////////////////////////////////////////////////////////
// STYLES, in production, these will be written to <script> tags
///////////////////////////////////////////////////////////////////////////////////
import styles from "./index.scss";

///////////////////////////////////////////////////////////////////////////////////////////////////////////
// GLOBAL VARIABLES & STRUCTURES
///////////////////////////////////////////////////////////////////////////////////////////////////////////

// the current year as configured in the slider
let currentYear = 2024;

// XXX: Manually list out the available ranges when timespan is 5
let rangesForTimespan5 = [
  [2000, 2004],
  [2005, 2009],
  [2010, 2014],
  [2015, 2019],
  [2020, 2024],
];

// global config
const CONFIG = {};
const DATA = {};
const LOOKUP = {};

// some data defaults on load and for reset
CONFIG.default_type = "ppp"; // PPP is the default data type
CONFIG.default_unit = "dollars"; // USD is the default unit
CONFIG.default_time = "year1"; // 1-year is the default timespan

// colors and styles
CONFIG.colors = {
  chart_orange: styles.chart_orange,
  chart_blue: styles.chart_blue,
  light_text: styles.light_text,
};

CONFIG.chart_style = {
  fontFamily: "Montserrat",
  fontSize: "12px",
  color: CONFIG.colors.light_text,
};

CONFIG.iconSizes = {
  0: 13,
  1: 11,
  2: 9,
  3: 6,
  4: 3.5,
};

CONFIG.binColors = {
  // blue-green scheme for positive:
  // https://gka.github.io/palettes/#/5|s|003ecc,1fe074|1fe074|1|1
  // pos: ['#003ecc', '#4567ba', '#538fa6', '#4cb78f', '#1fe074'],

  // brighter blue-green scheme for positive (using same end colors as above):
  // https://www.learnui.design/tools/data-color-picker.html
  pos: ["#003ecc", "#007af9", "#00a4ea", "#00c5b3", "#1fe074"],

  // orange-red scheme for negative:
  // https://gka.github.io/palettes/#/5|s|ff9600,94003a|ffffe0,ff005e,93003a|1|1
  neg: ["#ff9600", "#e47623", "#ca5730", "#af3537", "#94003a"],
};

CONFIG.nodata_radius = 2;

// a value to be used when we get Infinity or NaN out of a percent calculation
CONFIG.nothing_percent_value = "--%";

CONFIG.format = {
  string: function (s) {
    return s;
  },
  number: function (n) {
    return parseFloat(n).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  },
  // string to float with two decimals
  float: function (n) {
    return parseFloat(n).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  },
  // string to number with as many output decimals as input
  variable_float: function (n) {
    if (isNaN(n)) return n;
    let num = Math.round((+n + Number.EPSILON) * 100) / 100;
    if (num < 11) {
      return parseFloat(num).toLocaleString("en-US");
    } else {
      return parseFloat(num).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    }
  },
  dollars: function (m, type) {
    if (type != "gdp") {
      // PPP and EXR
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumSignificantDigits: 3,
        notation: "compact",
        compactDisplay: "short",
      }).format(m);
    } else {
      // GDP:
      let n = parseFloat(m);
      let f = n.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      if (f == "0.00") f = ">0";
      if (f == "-0") f = ">-0"; // shouldn't be a thing, since only % are negative here
      return `$${f}`;
    }
  },
  percent: function (n) {
    let p =
      parseFloat(n).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }) + "%";
    if (p == "Infinity%") return CONFIG.nothing_percent_value;
    if (p == "NaN%") return CONFIG.nothing_percent_value;
    if (p == "∞%") return CONFIG.nothing_percent_value;
    return p;
  },
  legend_percent: function (n, type, sign = "positive") {
    // differential rounding, depending on the input
    let z = 1000000;
    if (n < 10000000) z = 100000;
    if (n < 1000000) z = 10000;
    if (n < 100000) z = 1000;
    if (n < 10000) z = 10;
    if (n < 1000) z = 10;
    if (n < 100) z = 10;
    if (n < 10) z = 1;
    // same rules for negative, so the formatting is consistent
    if (n < 0) z = 1;
    if (n < -10) z = 10;
    if (n < -100) z = 10;
    if (n < -1000) z = 10;
    if (n < -10000) z = 1000;
    if (n < -100000) z = 10000;
    if (n < -1000000) z = 100000;

    let p = (Math.round(n / z) * z).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    // if between 1 and -1, nearest hundredths (0.xx).
    if (n < 1 && n > -1) {
      p = n.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    if (p == "Infinity") return CONFIG.nothing_percent_value;
    if (p == "NaN") return CONFIG.nothing_percent_value;
    if (p == "∞") return CONFIG.nothing_percent_value;
    if (p == "0.00" && sign == "positive") return "1%";
    if (p == "0.00" && sign == "negative") return "-1%";
    return p + "%";
  },
};

// track first load
CONFIG.first_load = true;

// a global object for summing data and holding results
CONFIG.country_sums_all = {};

///////////////////////////////////////////////////////////////////////////////////////////////////////////
///// INITIALIZATION: these functions are called when the page is ready,
///////////////////////////////////////////////////////////////////////////////////////////////////////////
$(document).ready(function () {
  // perform initialization steps
  initDataFormat(); // get data ready for use
  initDataControls(); // init the controls for units, currency, timespan
  initFilterControls(); // init the data filter controls
  initSliderControl(); // init the slider control
  initMap(); // regular Leaflet map setup
  initChart(); // init the chart
  initTooltips(); // init tippy tooltips

  // before we go further, wait for the map to fully load
  initState(); // init app state. This is the app "entry point"

  // initialize a resize handler
  $(window).on("resize", resize);
});

// resize everything: map, content divs, table
function resize() {
  // Do we need this?
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////
///// FUNCTIONS CALLED ON DOC READY
///////////////////////////////////////////////////////////////////////////////////////////////////////////

// Data formatting routines, to get the static, raw data files into the form we need it in
function initDataFormat(data) {
  // get country data from `world.json`
  DATA.worldjson = worldJson;

  // parse and keep a reference to the lookup
  let lookup = Papa.parse(lookupCsv, {
    header: true,
    skipEmptyLines: "greedy",
  });
  lookup.data.forEach(function (d) {
    LOOKUP[d.key] = d;
  });

  // parse the main data file to json
  let rawjson = Papa.parse(dataCsv, { header: true, skipEmptyLines: "greedy" });

  // Get the set of all country ISO codes to use in the country multiselect.  Note that the
  // set of countries could be different for each of the three main data types (PPP, EXR, GDP)
  // but we will show a unified list of countries regardless of what display mode is selected.
  // If data is not available for a country under the selected display mode, that is OK; the
  // country will not appear in the chart/map, but there will be no errors.  (In practice,
  // for the current data sets, the only difference in country sets between the 3 display modes
  // is that PPP has an "MI" / Mission Innovation item, while EXR and GDP do not.)
  let allCountries = new Set();
  ["ppp", "exr", "gdp"].forEach(function (type) {
    let data = rawjson.data.filter(function (d) {
      return d.type == type;
    });
    for (let item of data) {
      allCountries.add(item.iso);
    }
  });

  // Sort by country name and keep a reference
  DATA.countries = [...allCountries].sort(function (a, b) {
    return LOOKUP[a].value.localeCompare(LOOKUP[b].value);
  });

  // Get the set of all categories to use in the investment multiselect.
  let allCategories = new Set(rawjson.data.map((item) => item.category));
  let categories = [...allCategories].filter((c) => c); // remove blanks

  // Sort categories by the sort column and keep a reference
  DATA.categories = categories.sort(function (a, b) {
    return parseInt(LOOKUP[a]["sort"]) - parseInt(LOOKUP[b]["sort"]);
  });

  // Get min and max years from the data
  // Assume these are all the same for all three data types
  DATA.minyear = Math.min(...rawjson.data.map((o) => o.year));
  DATA.maxyear = Math.max(...rawjson.data.map((o) => o.year));

  // Keep a reference to the raw data
  DATA.rawdata = rawjson.data;
}

// init the controls for "Data Options"
function initDataControls() {
  // update the title/chart/map when unit option is changed
  $("input[type=radio][name=units]").on("input change", function (e) {
    // update the slider depending on the selected units and timespan (we don't need to
    // reset in this case, except if the units==percent and time==2000)
    updateSliderRange(/*resetToMax=*/ false);

    // update title/chart/map
    applyFilters();
  });

  // update the title/chart/map when currency option is changed
  $("input[type=radio][name=currency]").on("input change", function (e) {
    // show the investment type or category options corresponding to the selected currency;
    // if "gdp" then we disable the multiselect
    let currency = $('input[name="currency"]:checked').val();
    $("div.checkboxset-investment button").attr("disabled", currency == "gdp");

    // update title/chart/map
    applyFilters();
  });

  // update the title/chart/map when timespan is changed
  $('input[name="timespan"]').on("change", function () {
    // update the slider depending on the selected units and timespan
    updateSliderRange(/*resetToMax=*/ true);

    // update title/chart/map
    applyFilters();
  });

  // init the multiselect "Data Options" control that shows the radio buttons configured above
  MultiSelect(`checkboxset-data-options`, {
    allowSelectAll: false,
    selectedText: "selected",
    searchText: false,
  });
  $("#checkboxset-data-options").show();
}

// Init the "Data Filters": controls that filter the data for the map, chart, table, including the timeline
function initFilterControls() {
  // COUNTRIES
  // populate and init the country multiselect
  let countryFields = $(`div#checkboxset-country div.fields`);
  DATA.countries.forEach(function (c) {
    // skip MI (Mission Innovation), we only show this on the table
    if (c == "MI") return;
    let country = LOOKUP[c].value;
    let html = `<label><input class="search-input" type="checkbox" name="country" value="${c}" checked/> ${country}</label>`;
    countryFields.append(html);
  });

  // init the multiselect for countries
  MultiSelect(`checkboxset-country`, {
    allowSelectAll: "Select all/none",
    selectedText: "selected",
    searchText: false,
  });

  // INVESTMENT TYPE
  // populate the investment multiselect
  let investmentFields = $(`div#checkboxset-investment div.fields`);
  DATA.categories.forEach(function (c) {
    let category = LOOKUP[c].value;
    let html = `<label><input class="search-input" type="checkbox" name="investment_type" value="${c}" checked/> ${category}</label>`;
    investmentFields.append(html);
  });

  // init the multiselect for investment categories
  MultiSelect(`checkboxset-investment`, {
    allowSelectAll: "Select all/none",
    selectedText: "selected",
    searchText: false,
  });

  // update the title/chart/map immediately when any filter checkboxes are changed
  $('input[type="checkbox"]').on("input change", function (e) {
    applyFilters();
  });
}

// Init the slider that controls the current year or timespan
function initSliderControl() {
  // update the title/chart/map when unit options are changed
  $(".slider-container").on("input change", function (e) {
    // save the current slider position
    const sliderValue = e.target.value;
    currentYear = parseInt(sliderValue);

    // update the title/chart/map
    applyFilters();
  });
}

// Map inits
function initMap() {
  CONFIG.map = L.map("map", {
    maxZoom: CONFIG.maxzoom,
    scrollWheelZoom: false,
    attributionControl: false,
    keyboard: true,
  });

  let world = L.geoJson(DATA.worldjson, {
    style: {
      color: "#000",
      weight: 0.5,
      opacity: 0.2,
      fillColor: "#fff",
      fillOpacity: 0.8,
    },
  }).addTo(CONFIG.map);

  new LeafletCreditsControl({
    image: "./static/images/cpl-logo-white-75x52.svg",
    link: "http://www.climatepolicylab.org/",
    text: "Created by the<br/>Climate Policy Lab",
    width: "75",
    height: "60",
  }).addTo(CONFIG.map);

  // CONFIG.map.fitBounds(world);

  // set the initial lat/lon and zoom to make all data points visible
  CONFIG.map.setView([18, 0], 1);

  // create panes to hold layers
  // - create a pane for the country null values
  CONFIG.map.createPane("country-nulls");
  CONFIG.map.getPane("country-nulls").style.zIndex = 550;

  // - create a pane for the zero values
  CONFIG.map.createPane("country-zeros");
  CONFIG.map.getPane("country-zeros").style.zIndex = 525;

  // - create a pane for the regular map symbols
  CONFIG.map.createPane("country-shapes");
  CONFIG.map.getPane("country-shapes").style.zIndex = 500;

  // add empty layers to the map to hold country points/icons
  // these get populated in drawMap();
  CONFIG.country_nulls = L.featureGroup([], { pane: "country-nulls" }).addTo(
    CONFIG.map,
  );
  CONFIG.country_zeros = L.featureGroup([], { pane: "country-zeros" }).addTo(
    CONFIG.map,
  );
  CONFIG.country_shapes = L.featureGroup([], { pane: "country-shapes" }).addTo(
    CONFIG.map,
  );
}

// Chart inits
function initChart() {
  // XXX: Use a fixed width for y-axis labels so that the chart doesn't bounce around when
  // countries go in and out of view; this is empirically determined to encompass the longest
  // country name, which is currently "United Arab Emirates"
  const labelWidth = "130px";

  // Basic chart config, data, categories and other items will be updated at run time
  CONFIG.chart_config = {
    chart: {
      type: "bar",
    },
    accessibility: {
      keyboardNavigation: {
        // make screen reader read the chart's aria-label before entering
        order: ["container"],
      },
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      title: false,
      type: "category",
      labels: {
        useHTML: true,
        step: 1,
        animate: true,
        style: {
          ...CONFIG.chart_style,
          width: labelWidth,
        },
        formatter: function () {
          return `<span style="display: inline-block; text-align: right; width: ${labelWidth}">${this.value}</span>`;
        },
      },
    },
    plotOptions: {
      bar: {
        dataLabels: {
          enabled: false,
        },
      },
      series: {
        animation: false,
        pointPadding: 0.15,
        groupPadding: 0,
        borderWidth: 0,
        maxPointWidth: 30,
        shadow: false,
      },
    },
    legend: false,
    series: [
      {
        dataSorting: {
          enabled: true,
          sortKey: "y",
        },
      },
    ],
  };
}

// State
function initState() {
  // set the initial state of the title/chart/map based on the current control values
  applyFilters();

  CONFIG.first_load = false;
}

// Tooltips on the table "info" icons and related button inits
// not much to it... but we do have to delegate here, since these are added dynamically
function initTooltips() {
  tippy.delegate("body", {
    target: "[data-tippy-content]",
    allowHTML: true,
    // default is 'mouseenter focus', which should be accessible https://atomiks.github.io/tippyjs/v5/accessibility/
    // trigger: 'click',
    interactive: true,
    hideOnClick: false, // we need to be able to click on <a> links within
    onShow(instance) {
      tippy.hideAll({ exclude: instance });
    },
  });

  // we have to pass a message to the parent, which then opens the appropriate tab for us
  // direct anchor links are not working
  $("body").on("click", "button.info-button", function () {
    parent.postMessage("openDownloadTab", "*");
  });
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////
///// NAMED FUNCTIONS
///////////////////////////////////////////////////////////////////////////////////////////////////////////

// update the slider range depending on the current units and timespan
function updateSliderRange(resetToMax) {
  let units = $('input[name="units"]:checked').val();
  let timespan = $('input[name="timespan"]:checked').val();
  let step;
  let minYear, maxYear;
  let minYearString, maxYearString;
  let smallText;
  switch (timespan) {
    case "1":
    case "all":
      // show a single year in the labels and use a time step of 1 on the slider control
      step = 1;
      if (units == "percent" && timespan == "1") {
        minYear = 2001;
        maxYear = 2024;
      } else {
        minYear = 2000;
        maxYear = 2024;
      }
      maxYear = 2024;
      minYearString = minYear.toString();
      maxYearString = maxYear.toString();
      smallText = false;
      break;
    case "5":
      // show a year range in the labels and use a different time step on the slider control
      step = 5;
      minYear = 2000;
      maxYear = 2020;
      // note that we use `&#8209;` (non-breaking hyphen) to prevent the browser from wrapping
      // to a new line when space is limited
      minYearString = "2000&#8209;2004";
      maxYearString = "2020&#8209;2024";
      smallText = true;
      break;
  }
  $("#year-slider").attr("min", minYear);
  $("#year-slider").attr("max", maxYear);
  $("#year-slider").attr("step", step);
  $(".slider-container").toggleClass("small-text", smallText);
  $(".slider-min-year").html(minYearString);
  $(".slider-max-year").html(maxYearString);

  if (resetToMax) {
    // reset the current year to the max value
    currentYear = maxYear;
    $("#year-slider").val(maxYear);
  } else {
    // we don't have percent change data for the year 2000, so if slider is currently at 2000
    // and the units are switched to percent, nudge the current year to 2001, otherwise keep
    // the existing year
    if (timespan == "1" && units == "percent" && currentYear == 2000) {
      currentYear = 2001;
      $("#year-slider").val(2001);
    }
  }

  // disable the year slider when timespan is set to "all"
  $(".slider-container").toggleClass("disabled", timespan == "all");
  $("#year-slider").attr("disabled", timespan == "all");
}

// the main "search" or "filter" function
function filterData(calculateGlobalMinMax = true) {
  let units = $('input[name="units"]:checked').val();
  let type = $('input[name="currency"]:checked').val();
  let timespan = $('input[name="timespan"]:checked').val();

  let countries = $(`#checkboxset-country fieldset input.search-input:checked`)
    .map(function () {
      return this.value;
    })
    .get();

  // if the user clicks "Select none", show all countries temporarily until they select one or
  // more individual countries
  if (countries.length == 0) {
    countries = $(`#checkboxset-country fieldset input.search-input`)
      .map(function () {
        return this.value;
      })
      .get();
  }

  // initially always add in MI... we'll have to remove it later for the map and table
  countries = [...countries, "MI"];

  let categories = $(
    `#checkboxset-investment fieldset input.search-input:checked`,
  )
    .map(function () {
      return this.value;
    })
    .get();

  // if the user clicks "Select none", show all categories temporarily until they select one or
  // more individual categories
  if (categories.length == 0) {
    categories = $(`#checkboxset-investment fieldset input.search-input`)
      .map(function () {
        return this.value;
      })
      .get();
  }

  let years = getYearsFromTimespan(timespan, units);

  // Filter the data
  // base query
  let matches = DATA.rawdata.filter(function (d) {
    return d.type == type && countries.indexOf(d.iso) > -1;
  });

  // investment types or categories - skip this for GDP
  if (type != "gdp") {
    if (matches.length) {
      matches = matches.filter(function (d) {
        return categories.indexOf(d.category) > -1;
      });
    }
  }

  // Before we apply the year filter, keep a reference to the full (time) range of the data, see below
  let matches_allyears;
  if (calculateGlobalMinMax) {
    // clone matches, not by reference but by value
    matches_allyears = matches.map((a) => {
      return { ...a };
    });
  }

  // years
  // if there are no years, then we don't filter by year, as this is cumulative (all years)
  if (years && matches.length) {
    matches = matches.filter(function (d) {
      return years.indexOf(d.year) > -1;
    });
  }

  // next steps vary depending on if we are calcing total amount or percent change
  // country-sums are the sums for the selected set of years, this draws the chart/map
  // CONFIG.country_sums_all: sum across all years - this feeds the min/max scales for chart/map, but we don't want to recalculate this unless we have to
  if (calculateGlobalMinMax) {
    CONFIG.country_sums_all = {};
    getCountrySumsAllYears(matches_allyears, units, timespan);

    // get the range of values for the chart, map and legend (skipping nulls) across ALL YEARS
    CONFIG.current_min = Number.POSITIVE_INFINITY;
    CONFIG.current_max = Number.NEGATIVE_INFINITY;
    Object.keys(CONFIG.country_sums_all).forEach(function (child) {
      Object.keys(CONFIG.country_sums_all[child]).forEach(function (key) {
        if (key == "MI") return;
        let v = CONFIG.country_sums_all[child][key].total;
        if (v == null) return;
        if (v < CONFIG.current_min) CONFIG.current_min = v;
        if (v > CONFIG.current_max) CONFIG.current_max = v;
      });
    });
  }

  // it is possible to get min==max in which case, our buckets are undefined.
  // in that case, arbitrary "fix" by defining min as max/5
  if (CONFIG.current_max == CONFIG.current_min) {
    CONFIG.current_min = CONFIG.current_max / 5;
  }

  // get the yearkey from which to extract the current year of data from CONFIG.country_sums_all
  let yearskey;
  switch (timespan) {
    case "1":
      if (units == "dollars") yearskey = years[0];
      if (units == "percent") yearskey = `${years[0]}-${years[1]}`;
      break;
    case "5":
      yearskey = `${years[0]}-${years[years.length - 1]}`;
      break;
    case "all":
      yearskey = `${DATA.minyear}-${DATA.maxyear}`;
      break;
  }

  let country_sums = CONFIG.country_sums_all[yearskey];
  let mi, total;
  if (country_sums) {
    mi = country_sums["MI"];
    delete country_sums["MI"];
    delete country_sums["total"]; // TODO: lets not calc this if we are not using it
  }

  // set up the postive and negative ranges we'll use to bin data for the map
  let pos_range, neg_range;
  CONFIG.pos_ranges = [];
  CONFIG.neg_ranges = [];
  if (CONFIG.current_min > 0) {
    pos_range = CONFIG.current_max - CONFIG.current_min;
  } else {
    pos_range = CONFIG.current_max - 0;
    neg_range = 0 - CONFIG.current_min;
  }

  // our 5 intervals are defined on either the positive or negative range, whichever is greater
  // by definition the other (shorter) interval gets truncated
  let interval;
  let truncate_neg = false;
  let truncate_pos = false;
  if (neg_range && pos_range < neg_range) {
    interval = neg_range / 5;
    truncate_pos = true;
  } else {
    interval = pos_range / 5;
    truncate_neg = true;
  }

  // somewhat unexpected: we bin to up to five equal intervals, separately, for positive and negative values, depending on which range is greater.
  // That means dynamic legends for both positive and negative values, but at least one will always have 5 bins
  if (pos_range) {
    let current = 0;
    for (let i = 0; i < 5; i++) {
      let next = current + interval;
      if (i == 4) next = CONFIG.current_max; // otherwise rounding errors can produce a tiny neg number very close to 0
      if (truncate_pos) {
        // we will have less than the full five intervals in this series
        if (
          CONFIG.current_max > next ||
          (CONFIG.current_max > current && CONFIG.current_max < next)
        ) {
          // while the min value is within or beyond the range of current and next, keep adding intervals
          CONFIG.pos_ranges.unshift([next, current]);
        }
      } else {
        // No truncation: push all five
        CONFIG.pos_ranges.unshift([next, current]);
      }
      current = next;
    }
  }
  if (neg_range) {
    let current = 0;
    for (let i = 0; i < 5; i++) {
      let next = current - interval;
      if (i == 4) next = CONFIG.current_min; // otherwise rounding errors can produce a tiny difference from CONFIG.current_min
      // we will have less than the full five intervals in this series
      if (truncate_neg) {
        if (
          CONFIG.current_min < next ||
          (CONFIG.current_min < current && CONFIG.current_min > next)
        ) {
          // while the min value is within or beyond the range of current and next, keep adding intervals
          CONFIG.neg_ranges.push([current, next]);
        }
      } else {
        // No truncation: push all five
        CONFIG.neg_ranges.push([current, next]);
      }
      current = next;
    }
    if (!CONFIG.neg_ranges.length) debugger;
  }

  // Do everything with the results
  drawChart(country_sums, units, type, years);
  drawMap(country_sums, units, type, years);
  drawLegend(country_sums, units, type, years);
  // add MI back in for the table only
  let tabledata = { ...country_sums, MI: { ...mi } };
  drawTable(tabledata, units, type, years);
}

// calculate country sums on matches for every single time step to find the max country sum across all years
// the way we do this differs significantly for dollars vs. percent and for 1 year timespan vs 5 year
function getCountrySumsAllYears(matches, units, timespan) {
  if (units == "dollars") {
    // calculate country sums on matches for every country for every single time step
    if (timespan == "1") {
      for (let year = DATA.minyear; year <= DATA.maxyear; year++) {
        // filter the data to this year, and run the sums
        year = year.toString();
        sumDollarsByYears(timespan, [year], matches, year);
      }
    } else if (timespan == "5") {
      rangesForTimespan5.forEach(function (range) {
        let min = range[0];
        let max = range[1];
        let years = Array.from({ length: max + 1 - min }, (v, k) => min + k);
        years = years.map((n) => {
          return String(n);
        });
        sumDollarsByYears(timespan, years, matches, `${min}-${max}`);
      });
    } else if (timespan == "all") {
      sumDollarsByYears(
        timespan,
        null,
        matches,
        `${DATA.minyear}-${DATA.maxyear}`,
      );
    }
  } else {
    // percents: here we have to track the year range and calculate percent change between them
    if (timespan == "1") {
      for (let year = DATA.minyear - 1; year <= DATA.maxyear; year++) {
        let year1 = year.toString();
        let year2 = (year + 1).toString();
        calcPercentChangeByYears(year1, year2, matches);
      }
    } else if (timespan == "5") {
      rangesForTimespan5.forEach(function (range) {
        let year1 = range[0].toString();
        let year2 = range[1].toString();
        calcPercentChangeByYears(year1, year2, matches);
      });
    } else if (timespan == "all") {
      let year1 = DATA.minyear.toString();
      let year2 = DATA.maxyear.toString();
      calcPercentChangeByYears(year1, year2, matches);
    }
  }
}

// generic function to filter and sum dollars on countries
function sumDollarsByYears(timespan, years, matches, yearkey) {
  let filtered = years
    ? matches.filter(function (d) {
        return years.indexOf(d.year) > -1;
      })
    : matches;
  filtered.forEach(function (d) {
    // add keys to the output, if not there already
    if (!CONFIG.country_sums_all.hasOwnProperty(yearkey)) {
      CONFIG.country_sums_all[yearkey] = {};
    }
    let iso = d.iso;
    if (!CONFIG.country_sums_all[yearkey].hasOwnProperty(iso)) {
      CONFIG.country_sums_all[yearkey][iso] = { total: null };
    }
    let category = d.category;
    if (!CONFIG.country_sums_all[yearkey][iso].hasOwnProperty(category)) {
      CONFIG.country_sums_all[yearkey][iso][category] = {
        total: null,
        sources: [],
      };
    }
    if (!CONFIG.country_sums_all[yearkey].hasOwnProperty("total")) {
      CONFIG.country_sums_all[yearkey]["total"] = { value: null };
    }
    // get the value. If it's blank (no data), skip it, otherwise add it to the sum
    if (d.value == "") return;
    let value = parseFloat(d.value);
    let source = d.source;
    CONFIG.country_sums_all[yearkey][iso]["total"] += value;
    CONFIG.country_sums_all[yearkey][iso][category]["total"] += value;
    CONFIG.country_sums_all[yearkey]["total"]["value"] += value;
    if (source)
      CONFIG.country_sums_all[yearkey][iso][category]["sources"].push(source);
  });
}

function calcPercentChangeByYears(year1, year2, matches) {
  let years = [year1, year2];
  let range = `${year1}-${year2}`;

  // use a temporary object to hold the years, so that these keys don't interfere with drawing the table categories
  let temp_sums = {};

  // filter to the matching pair of years
  let yearsfilter = matches.filter(function (d) {
    return years.indexOf(d.year) > -1;
  });
  yearsfilter.forEach(function (d) {
    if (!CONFIG.country_sums_all.hasOwnProperty(range)) {
      CONFIG.country_sums_all[range] = {};
      temp_sums[range] = {};
    }

    // get the country and add it to the output, if not there already
    let iso = d.iso;
    if (!CONFIG.country_sums_all[range].hasOwnProperty(iso)) {
      CONFIG.country_sums_all[range][iso] = { total: null };
      temp_sums[range][iso] = { [year1]: null, [year2]: null };
    }

    // get the category and add it to the output, if not there already
    let category = d.category;
    if (!CONFIG.country_sums_all[range][iso].hasOwnProperty(category)) {
      CONFIG.country_sums_all[range][iso][category] = {
        total: null,
        sources: [],
      };
      temp_sums[range][iso][category] = { [year1]: null, [year2]: null };
    }

    // get the value. If it's blank (no data), skip it, otherwise add it to the sum
    if (d.value == "") return;
    let value = parseFloat(d.value);

    // increment the total, and total per category
    if (d.year == year1) temp_sums[range][iso][year1] += value;
    if (d.year == year2) temp_sums[range][iso][year2] += value;
    if (d.year == year1) temp_sums[range][iso][category][year1] += value;
    if (d.year == year2) temp_sums[range][iso][category][year2] += value;

    // Category total percent change:
    if (
      temp_sums[range][iso][category][year1] &&
      temp_sums[range][iso][category][year2]
    ) {
      // calc the percent change
      // (New - Original) / Original * 100
      let change =
        ((temp_sums[range][iso][category][year2] -
          temp_sums[range][iso][category][year1]) /
          temp_sums[range][iso][category][year1]) *
        100;
      CONFIG.country_sums_all[range][iso][category]["total"] = change;
    } else {
      CONFIG.country_sums_all[range][iso][category]["total"] = null;
    }

    // Total percent change:
    if (temp_sums[range][iso][year1] && temp_sums[range][iso][year2]) {
      // calc the percent change
      // (New - Original) / Original * 100
      let change =
        ((temp_sums[range][iso][year2] - temp_sums[range][iso][year1]) /
          temp_sums[range][iso][year1]) *
        100;
      CONFIG.country_sums_all[range][iso]["total"] = change;
    } else {
      CONFIG.country_sums_all[range][iso]["total"] = null;
    }

    // sources
    if (d.source)
      CONFIG.country_sums_all[range][iso][category]["sources"].push(d.source);
  });
}

// you guessed it: draw the map
function drawMap(data, units, type, years) {
  // clear any existing symbols from the map
  CONFIG.country_shapes.clearLayers();
  CONFIG.country_zeros.clearLayers();
  CONFIG.country_nulls.clearLayers();
  // iterate the data, and add points to the map
  let values = [];
  Object.keys(data).forEach(function (key) {
    let value = data[key].total;
    let lat = LOOKUP[key].lat;
    let lng = LOOKUP[key].lng;
    let country = LOOKUP[key].value;
    let bin;

    // figure out which "bin" the current value is in
    if (value != null) {
      bin = getCurrentValueBin(value);
    }

    // Create and add markers and icons to the map for each data point
    // marker style and type depends on the value, null, 0, positive, negative...
    let shapemarker;
    if (value == null) {
      shapemarker = L.shapeMarker([lat, lng], {
        pane: "country-nulls",
        fillColor: "#FFF",
        color: "#999",
        fillOpacity: 0.5,
        stroke: true,
        weight: 1,
        shape: "circle",
        radius: 4,
      }).addTo(CONFIG.country_nulls);
    } else if (value == 0) {
      shapemarker = L.shapeMarker([lat, lng], {
        pane: "country-zeros",
        fillColor: "#959595",
        color: "#959595",
        fillOpacity: 1,
        stroke: true,
        weight: 1,
        shape: "circle",
        radius: 4,
      }).addTo(CONFIG.country_zeros);
    } else {
      let fillColor =
        value > 0 ? CONFIG.binColors.pos[bin] : CONFIG.binColors.neg[4 - bin];
      let shape = value > 0 ? "triangle-up" : "triangle-down";
      shapemarker = L.shapeMarker([lat, lng], {
        pane: "country-shapes",
        fillColor,
        color: "#ddd",
        fillOpacity: 1,
        stroke: true,
        weight: 1,
        shape,
        radius: CONFIG.iconSizes[bin],
      }).addTo(CONFIG.country_shapes);
    }

    let labels = getDynamicDataLabels(units, years, type);
    let format = units;
    let formatted =
      value == null ? "<i>No Data</i>" : CONFIG.format[format](value, type);
    let popup = `<div style="display: inline-block;font-size: 130%; font-weight: 600; margin-bottom: 5px; border-bottom: 1px solid #999;">${country}</div><div>${labels[0]}:</div><div>${formatted}</div>`;
    shapemarker.bindPopup(popup);
  });
}

// given a value (dollars, percent...) return the "bin" or interval that it falls within
// this is used to assign triangle markers to positive and negative values
function getCurrentValueBin(value) {
  let bin;
  if (value > 0) {
    CONFIG.pos_ranges.forEach(function (range, i) {
      if (value <= range[0] && value >= range[1]) bin = i;
    });
  } else {
    CONFIG.neg_ranges.forEach(function (range, i) {
      // bins here are reversed from the pos_ranges, as in the legend:
      // first range is for the smallest symbol, last range for the largest
      if (value <= range[0] && value >= range[1]) bin = 4 - i;
    });
  }
  return bin;
}

// you guessed it: draw the legend
function drawLegend(data, units, type, years) {
  // this creates a CSS `background-image` style with a URL-encoded SVG triangle definition;
  // use https://yoksel.github.io/url-encoder/ to create the URL-encoded SVG
  function triangleStyle(dir, color) {
    let encodedColor = color.replace("#", "%23");
    if (dir === "up") {
      return `background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' id='triangleN' viewBox='0 0 100 100'%3E%3Cpolygon points='0 100, 100 100 50 0' fill='${encodedColor}' stroke='%23ddd' stroke-width='4'/%3E%3C/svg%3E");`;
    } else {
      return `background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' id='triangleN' viewBox='0 0 100 100'%3E%3Cpolygon points='0 0, 100 0 50 100' fill='${encodedColor}' stroke='%23ddd' stroke-width='4'/%3E%3C/svg%3E");`;
    }
  }

  // format zero text based on units
  let zerotext = units == "dollars" ? "$0" : "0%";
  $("div#zero-text").text(zerotext);

  // Positive values: add triangles and text for each data interval
  let target = $("div#legend-symbols-pos").empty();
  CONFIG.pos_ranges.forEach(function (range, i) {
    let outer = $("<div>", {
      class: "legend-interval-outer d-flex align-items-center",
    }).appendTo(target);
    // triangle: if we aren't showing all 5, then we need to offset the index, in reverse order. Fun yeah?
    let offset = 5 - CONFIG.pos_ranges.length;
    let j = i + offset;
    $("<div>", {
      class: `triangle-symbol triangle-pos-${j}`,
      style: triangleStyle("up", CONFIG.binColors.pos[j]),
    }).appendTo(outer);
    // text
    let format = units == "percent" ? "legend_percent" : units;
    let text = `${CONFIG.format[format](range[1], type)} to ${CONFIG.format[format](range[0], type)}`;
    $("<div>", {
      text: text,
      class: "legend-range",
    }).appendTo(outer);
  });

  // Negative values: add triangles and text for each data interval
  if (CONFIG.neg_ranges.length) {
    // clear and show the div
    let target = $("div#legend-symbols-neg").empty().show();
    CONFIG.neg_ranges.forEach(function (range, i) {
      let outer = $("<div>", {
        class: "legend-interval-outer d-flex align-items-center",
      }).appendTo(target);
      // triangle
      let j = 4 - i;
      $("<div>", {
        class: `triangle-symbol triangle-neg-${j}`,
        style: triangleStyle("down", CONFIG.binColors.neg[i]),
      }).appendTo(outer);
      // text
      let format = units == "percent" ? "legend_percent" : units;
      let text = `${CONFIG.format[format](range[0], type, "negative")} to ${CONFIG.format[format](range[1], type, "negative")}`;
      $("<div>", {
        text: text,
        class: "legend-range",
      }).appendTo(outer);
    });
  } else {
    // no negative vals, hide the negative symbol container
    $("div#legend-symbols-neg").hide();
    $("div#legend-ranges-neg").hide();
  }
}

// you guessed it: draw the chart
function drawChart(data, units, type, years) {
  // transform data into an object we can sort and use in HC
  let countrydata = [];
  Object.keys(data).forEach(function (key) {
    if (data[key].total == null) return; // skip nulls
    countrydata.push({
      iso: key,
      countryname: LOOKUP[key].value,
      value: data[key].total,
    });
  });

  // extract the values (sums) for the bars
  countrydata = countrydata.map(function (m) {
    return [m.countryname, m.value];
  });
  // set the series name, for the tooltip
  // get the dynamic series name for the tooltips
  let seriesname = getDynamicDataLabels(units, years, type)[0];

  // only initialize the chart itself on first load
  if (CONFIG.first_load) {
    // initialize the Highcharts accessibility module
    highchartsAccessibility(Highcharts);

    // override Highcharts' default aria-label with a more custom description
    Highcharts.setOptions({
      lang: {
        accessibility: {
          chartContainerLabel:
            "This bar chart shows a rank-ordered list of countries that meet your filters, showing either the sum of RD&D funding for the selected timespan and investment types or the percent change of that sum compared to the previous period. Use the table below the chart and map to review the country totals for each of the investment types you have selected.",
        },
      },
    });

    CONFIG.chart = Highcharts.chart("chart", CONFIG.chart_config);
  }

  // set the data format for formatting values in tooltips, axes, etc.
  let format = units;

  // configure the bar colors based on the map bin ranges
  let zones = [];
  // XXX: Revisit the zones (there might be an off-by-one issue here)
  zones.push({
    value: -9999999999999,
    color: CONFIG.binColors.neg[CONFIG.neg_ranges.length - 1],
  });
  for (let i = CONFIG.neg_ranges.length - 1; i >= 0; i--) {
    let range = CONFIG.neg_ranges[i];
    zones.push({
      value: range[0],
      color: CONFIG.binColors.neg[i],
    });
  }
  for (let i = CONFIG.pos_ranges.length - 1; i >= 0; i--) {
    let range = CONFIG.pos_ranges[i];
    zones.push({
      value: range[0],
      color: CONFIG.binColors.pos[i],
    });
  }
  // XXX: Revisit the zones (there might be an off-by-one issue here)
  zones.push({
    value: 9999999999999,
    color: CONFIG.binColors.pos[0],
  });

  // .. after first load, we only update the things we need on the existing chart config
  // some things have to be updated via the chart.update method
  CONFIG.chart.update({
    title: { text: "" },
    tooltip: {
      useHTML: true,
      formatter: function () {
        return `<span style="font-weight:600;">${this.key}</span><br/> <span>${seriesname}:</span><br/><span style="font-size:130%">${CONFIG.format[
          format
        ](this.y, type)}</span>`;
      },
    },

    yAxis: [
      {
        max: CONFIG.current_max,
        min: CONFIG.current_min,
        title: false,
        showLastLabel: false,
        labels: {
          step: 2,
          useHTML: true,
          style: CONFIG.chart_style,
          formatter: function () {
            return `<span>${CONFIG.format[format](this.value, type)}</span>`;
          },
        },
      },
      {
        title: false,
        showLastLabel: false,
        labels: {
          y: 10,
          step: 2,
          useHTML: true,
          style: CONFIG.chart_style,
          formatter: function () {
            return `<span>${CONFIG.format[format](this.value, type)}</span>`;
          },
          opposite: true,
          linkedTo: 0,
        },
      },
    ],

    series: {
      zones,
    },
  });

  // update the series with setData
  CONFIG.chart.series[0].setData(countrydata, true, false);
}

// draw and update the table. Here we work with the raw matches, not the country sums
function drawTable(country_sums, units, type, years) {
  // get a list of all countries in the set
  let isos = Object.keys(country_sums);
  // sort the isos by their country name, but make sure 'MI' is last
  isos = isos.sort(function (a, b) {
    if (a == "MI") return 1;
    if (b == "MI") return -1;
    return LOOKUP[a].value.localeCompare(LOOKUP[b].value);
  });

  // get the list of categories in the set
  let categories = [];
  isos.forEach(function (i) {
    let c = Object.keys(country_sums[i]);
    categories.push(...c);
  });
  // get unique values
  categories = [...new Set(categories)];
  // remove "sources" from categories, this isn't shown directly in the table
  // also remove "" as this comes in when type is 'gdp'
  categories = categories.filter(function (d) {
    return d !== "sources" && d !== "";
  });
  categories = categories.sort(function (a, b) {
    return LOOKUP[a].sort - LOOKUP[b].sort;
  });

  // construct the table data by transforming the incoming data object
  let tabledata = [];
  isos.forEach(function (iso) {
    // always start off the row with the country in the first cell
    let row = [iso];
    // iterate categories and filter the data to this country and this category
    categories.forEach(function (category) {
      let value, sources;
      if (category == "total") {
        value = country_sums[iso]["total"];
        sources = "";
      } else {
        // XXX: Data not defined for MI when currency option is "Exchange rate", so we
        // need to check if data is defined here
        if (country_sums[iso][category]) {
          value = country_sums[iso][category]["total"];
          sources = country_sums[iso][category]["sources"];
        } else {
          value = 0;
          sources = "";
        }
      }
      let source = null;
      if (sources) {
        // remove duplicates and join
        sources = [...new Set(sources)];
        source = sources.join(", ");
      }
      row.push({
        iso: iso,
        category: category,
        value: value,
        source: source,
      });
    });

    // all set with this row, add it to tabledata
    tabledata.push(row);
  });

  // Now update the table data itself
  let target = $("table#country-data").empty();
  // header
  let thead = $("<thead>").appendTo(target);
  let thr = $("<tr>").appendTo(thead);
  // table headers: the country, followed by all categories, and the total
  let tableheaders = ["Country", ...categories];

  tableheaders.forEach(function (c, i) {
    // don't format the country name, only the category names
    let text = i == 0 ? c : LOOKUP[c].value;
    $("<th>", {
      text: text,
    }).appendTo(thr);
  });

  // Update the table body row by row
  let tbody = $("<tbody>").appendTo(target);

  // set the format
  let format = units;

  tabledata.forEach(function (row) {
    let classname = row[0] == "MI" ? "mi" : "";
    let tr = $("<tr>", { class: classname }).appendTo(tbody);
    row.forEach(function (cell, i) {
      let value;
      // First cell is the country name: look up the full name from the ISO
      if (i == 0) {
        value = LOOKUP[cell].value;
      } else {
        // For the remaining cells, we need to format the value
        value = cell.value;
        if (value == null || value == "") {
          value = "No Data";
        } else {
          value = CONFIG.format[format](value, type);
        }
      }
      // set the initial html value, typically just the value of the cell
      let html = `<span>${value}</span>`;

      // conditionally append icons for MI, EU, Source info...
      if (i == 0) {
        // MI and EU can only get appended to the first column
        if (LOOKUP[cell].mi) html += '<span class="table-icon mi-icon"></span>';
        if (LOOKUP[cell].eu) html += '<span class="table-icon eu-icon"></span>';
      } else {
        // info icons for "sources" can be appended to any data cell, when a "source" is present
        if (cell.source) {
          html += `<span tabindex="0" data-tippy-content="<b>Source</b>: ${cell.source}<br/><button type='button' class='info-button'>Learn more and suggest new data</button>" class="table-icon info-icon"></span>`;
        }
      }

      // All set! Create the table cell markup, and append it to the row
      let td = $("<td>", {
        html: html,
      }).appendTo(tr);
    });
  });
}

// Update the chart and map to reflect the current filter, data options, and slider position
function applyFilters() {
  // update the dynamic title at the top of the vis
  let units = $('input[name="units"]:checked').val();
  let timespan = $('input[name="timespan"]:checked').val();
  let years = getYearsFromTimespan(timespan, units);
  let type = $('input[name="currency"]:checked').val();
  let labels = getDynamicDataLabels(units, years, type);
  $("div#big-label h2").html(labels[0]);
  $("div#big-sublabel").html(labels[1]);

  // do the search, which triggers redrawing everything on the selected year
  filterData();
}

// Get dynamic data label for the chart tooltip, and also for the main datavis "title" near the top of the app
function getDynamicDataLabels(units, years, type) {
  function singleYearSpan(year) {
    return `<span class="active-time">${year}</span>`;
  }

  function multiYearSpan(minYear, maxYear) {
    // note that we use `&#8209;` (non-breaking hyphen) to prevent the browser from wrapping
    // to a new line when space is limited
    return `<span class="active-time time-range">${minYear}&#8209;${maxYear}</span>`;
  }

  let sublabel = `Measured in ${type == "gdp" ? "$ per US$1000 GDP" : "2024 US$"}`;
  if (units == "dollars") {
    let label;
    if (years == null) {
      label = "Global Public Energy RD&D Cumulative Investment";
    } else {
      let len = years.length;
      if (len > 1) {
        label = `Global Public Energy RD&D Expenditures, ${multiYearSpan(years[0], years[len - 1])}`;
      } else if (len == 1) {
        label = `Global Public Energy RD&D Expenditures, ${singleYearSpan(years[0])}`;
      }
    }
    return [label, sublabel];
  } else {
    // units are percent
    let minYear, maxYear;
    if (years == null) {
      minYear = DATA.minyear;
      maxYear = DATA.maxyear;
    } else if (years.length > 1) {
      minYear = years[0];
      maxYear = years[years.length - 1];
    } else if (years.length == 1) {
      minYear = parseInt(years[0]) - 1;
      maxYear = years[0];
    }
    return [
      `Global Public Energy RD&D Percent Change, ${multiYearSpan(minYear, maxYear)}`,
      sublabel,
    ];
  }
}

// given a timespan of "1", "5", "all", return a years array based on the current slider year
function getYearsFromTimespan(timespan, units) {
  switch (timespan) {
    case "all":
      return null;
    case "1": {
      if (units == "percent") {
        let year1 = (currentYear - 1).toString();
        let year2 = currentYear.toString();
        return [year1, year2];
      } else {
        return [currentYear.toString()];
      }
    }
    case "5": {
      // the slider currently controls the min year of the time range
      let min = currentYear;
      let max = currentYear + 4;
      let nums = Array.from({ length: max + 1 - min }, (v, k) => min + k);
      return nums.map((n) => {
        return String(n);
      });
    }
    default:
      throw new Error("Invalid timespan");
  }
}
